/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Who the upstream access credential belongs to. subject means each caller connects their own upstream account; self means the client holds one credential for itself, obtained with the client_credentials grant, that every caller shares.
 */
export const CredentialOwner = {
  Subject: "subject",
  Self: "self",
} as const;
/**
 * Who the upstream access credential belongs to. subject means each caller connects their own upstream account; self means the client holds one credential for itself, obtained with the client_credentials grant, that every caller shares.
 */
export type CredentialOwner = OpenEnum<typeof CredentialOwner>;

/**
 * Identifier used as the aud claim in private_key_jwt assertions. Null resolves to issuer.
 */
export const TokenEndpointAuthAudienceFormat = {
  Issuer: "issuer",
  TokenEndpoint: "token_endpoint",
} as const;
/**
 * Identifier used as the aud claim in private_key_jwt assertions. Null resolves to issuer.
 */
export type TokenEndpointAuthAudienceFormat = OpenEnum<
  typeof TokenEndpointAuthAudienceFormat
>;

/**
 * How the client authenticates at the issuer's token endpoint. Null resolves to client_secret_basic at runtime.
 */
export const RemoteSessionClientTokenEndpointAuthMethod = {
  ClientSecretBasic: "client_secret_basic",
  ClientSecretPost: "client_secret_post",
  None: "none",
  PrivateKeyJwt: "private_key_jwt",
} as const;
/**
 * How the client authenticates at the issuer's token endpoint. Null resolves to client_secret_basic at runtime.
 */
export type RemoteSessionClientTokenEndpointAuthMethod = OpenEnum<
  typeof RemoteSessionClientTokenEndpointAuthMethod
>;

/**
 * A remote_session_client record. client_secret_encrypted is never returned.
 */
export type RemoteSessionClient = {
  /**
   * Upstream OAuth audience sent on the authorize redirect and token exchange. Null omits the audience parameter.
   */
  audience?: string | undefined;
  /**
   * The redirect URI this client registers with its upstream provider. It never changes after the client is created. Absent on global clients and on clients with credential_owner self, which have no callback.
   */
  callbackUrl?: string | undefined;
  /**
   * The client_id used to identify this client at the issuer's token and authorization endpoints.
   */
  clientId: string;
  clientIdIssuedAt?: Date | undefined;
  /**
   * When set, the client is in Client ID Metadata Document (CIMD) mode: Speakeasy hosts its OAuth client metadata document at this URL and uses it as the client_id. Null for non-CIMD clients.
   */
  clientIdMetadataUri?: string | undefined;
  /**
   * Null when the secret does not expire.
   */
  clientSecretExpiresAt?: Date | undefined;
  createdAt: Date;
  /**
   * Who the upstream access credential belongs to. subject means each caller connects their own upstream account; self means the client holds one credential for itself, obtained with the client_credentials grant, that every caller shares.
   */
  credentialOwner: CredentialOwner;
  /**
   * Administrator-chosen name for the client. Null when unset, in which case the client is shown by its client_id.
   */
  displayName?: string | undefined;
  /**
   * The redirect URI to register in the customer identity provider's app when this client is a user session issuer's trusted sign-in client. Present only on organization-owned clients outside any project and not managed by an identity provider connection, the only clients a user session issuer can trust.
   */
  federatedCallbackUrl?: string | undefined;
  /**
   * Recorded effective registration grants. Null means unknown; an empty array means no recorded grants.
   */
  grantTypes: Array<string> | null;
  /**
   * The remote_session_client id.
   */
  id: string;
  /**
   * The organization JSON Web Key Set attached to this client, set on create or through attachKeySet and detachKeySet. Null when no key set is attached.
   */
  jsonWebKeySetId?: string | undefined;
  /**
   * Whether the client was registered upstream with the legacy callback URL. The authorize leg then sends that URL and a JSON state instead of the current callback. Cleared when the client is rotated.
   */
  legacyCallbackUrl: boolean;
  /**
   * The owning organization id. Empty for legacy rows not yet backfilled and global clients.
   */
  organizationId: string;
  /**
   * The owning project id. Empty for organization-level and global clients.
   */
  projectId: string;
  /**
   * The owning remote_session_issuer id.
   */
  remoteSessionIssuerId: string;
  /**
   * Explicit upstream OAuth scopes the dance requests for this client. Null falls back to the issuer's scopes_supported.
   */
  scope?: Array<string> | undefined;
  /**
   * Identifier used as the aud claim in private_key_jwt assertions. Null resolves to issuer.
   */
  tokenEndpointAuthAudienceFormat?: TokenEndpointAuthAudienceFormat | undefined;
  /**
   * How the client authenticates at the issuer's token endpoint. Null resolves to client_secret_basic at runtime.
   */
  tokenEndpointAuthMethod?:
    | RemoteSessionClientTokenEndpointAuthMethod
    | undefined;
  updatedAt: Date;
  /**
   * When the issuer's token endpoint last answered invalid_client for this client_id, meaning the issuer no longer recognizes the registration. Null while the registration is in good standing; cleared by a successful rotation, a successful refresh, or a replaced secret.
   */
  upstreamRejectedAt?: Date | undefined;
  /**
   * The user_session_issuers this client is attached to via the join table. Empty for a standalone client with no attachments.
   */
  userSessionIssuerIds: Array<string>;
};

/** @internal */
export const CredentialOwner$inboundSchema: z.ZodMiniType<
  CredentialOwner,
  unknown
> = openEnums.inboundSchema(CredentialOwner);

/** @internal */
export const TokenEndpointAuthAudienceFormat$inboundSchema: z.ZodMiniType<
  TokenEndpointAuthAudienceFormat,
  unknown
> = openEnums.inboundSchema(TokenEndpointAuthAudienceFormat);

/** @internal */
export const RemoteSessionClientTokenEndpointAuthMethod$inboundSchema:
  z.ZodMiniType<RemoteSessionClientTokenEndpointAuthMethod, unknown> = openEnums
    .inboundSchema(RemoteSessionClientTokenEndpointAuthMethod);

/** @internal */
export const RemoteSessionClient$inboundSchema: z.ZodMiniType<
  RemoteSessionClient,
  unknown
> = z.pipe(
  z.object({
    audience: z.optional(z.string()),
    callback_url: z.optional(z.string()),
    client_id: z.string(),
    client_id_issued_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    client_id_metadata_uri: z.optional(z.string()),
    client_secret_expires_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    credential_owner: CredentialOwner$inboundSchema,
    display_name: z.optional(z.string()),
    federated_callback_url: z.optional(z.string()),
    grant_types: z.nullable(z.array(z.string())),
    id: z.string(),
    json_web_key_set_id: z.optional(z.string()),
    legacy_callback_url: z.boolean(),
    organization_id: z.string(),
    project_id: z.string(),
    remote_session_issuer_id: z.string(),
    scope: z.optional(z.array(z.string())),
    token_endpoint_auth_audience_format: z.optional(
      TokenEndpointAuthAudienceFormat$inboundSchema,
    ),
    token_endpoint_auth_method: z.optional(
      RemoteSessionClientTokenEndpointAuthMethod$inboundSchema,
    ),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    upstream_rejected_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    user_session_issuer_ids: z.array(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "callback_url": "callbackUrl",
      "client_id": "clientId",
      "client_id_issued_at": "clientIdIssuedAt",
      "client_id_metadata_uri": "clientIdMetadataUri",
      "client_secret_expires_at": "clientSecretExpiresAt",
      "created_at": "createdAt",
      "credential_owner": "credentialOwner",
      "display_name": "displayName",
      "federated_callback_url": "federatedCallbackUrl",
      "grant_types": "grantTypes",
      "json_web_key_set_id": "jsonWebKeySetId",
      "legacy_callback_url": "legacyCallbackUrl",
      "organization_id": "organizationId",
      "project_id": "projectId",
      "remote_session_issuer_id": "remoteSessionIssuerId",
      "token_endpoint_auth_audience_format": "tokenEndpointAuthAudienceFormat",
      "token_endpoint_auth_method": "tokenEndpointAuthMethod",
      "updated_at": "updatedAt",
      "upstream_rejected_at": "upstreamRejectedAt",
      "user_session_issuer_ids": "userSessionIssuerIds",
    });
  }),
);

export function remoteSessionClientFromJSON(
  jsonString: string,
): SafeParseResult<RemoteSessionClient, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RemoteSessionClient$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RemoteSessionClient' from JSON`,
  );
}
